import React, { useEffect, useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Storage as StorageIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../utils/config';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';

interface QuickBooksTable {
  tableName: string;
  displayName: string;
  recordCount: number;
}

interface TablesResponse {
  success: boolean;
  tables: QuickBooksTable[];
  totalTables: number;
  error?: string;
  message?: string;
}

export function QuickBooksTablesPage() {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tables, setTables] = useState<QuickBooksTable[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchTables = async () => {
      if (cancelled) return;
      
      setLoading(true);
      setError(null);

      try {
        const response = await authenticatedFetch(`${API_URL}/api/quickbooks/tables`);
        
        if (cancelled) return;
        
        if (response) {
          const data: TablesResponse = await response.json();
          
          if (!cancelled) {
            if (data.success) {
              setTables(data.tables);
            } else {
              setError(data.error || 'Failed to load tables');
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching tables:', err);
          setError(err instanceof Error ? err.message : 'Failed to load tables');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTables();

    return () => {
      cancelled = true;
    };
  }, [authenticatedFetch]);

  const handleTableClick = (tableName: string) => {
    navigate(`/admin/quickbooks/tables/${tableName}`);
  };

  const getCategoryColor = (tableName: string): 'primary' | 'secondary' | 'warning' | 'info' | 'success' => {
    if (tableName.includes('sync_') || tableName.includes('oauth_') || tableName.includes('entity_config')) {
      return 'warning'; // System tables
    }
    if (tableName.includes('company_')) {
      return 'info'; // Company tables
    }
    if (tableName.includes('customer') || tableName.includes('vendor')) {
      return 'secondary'; // Contact tables
    }
    if (tableName.includes('invoice') || tableName.includes('bill') || tableName.includes('payment')) {
      return 'primary'; // Transaction tables
    }
    return 'success'; // Other data tables
  };

  const getCategory = (tableName: string): string => {
    if (tableName.includes('sync_') || tableName.includes('oauth_') || tableName.includes('entity_config')) {
      return 'System';
    }
    if (tableName.includes('company_')) {
      return 'Company';
    }
    if (tableName.includes('customer') || tableName.includes('vendor')) {
      return 'Contacts';
    }
    if (tableName.includes('invoice') || tableName.includes('bill') || tableName.includes('payment') || 
        tableName.includes('estimate') || tableName.includes('credit_memo') || tableName.includes('refund')) {
      return 'Transactions';
    }
    if (tableName.includes('item') || tableName.includes('product') || tableName.includes('service')) {
      return 'Items';
    }
    if (tableName.includes('account') || tableName.includes('tax') || tableName.includes('term') || 
        tableName.includes('class') || tableName.includes('department')) {
      return 'Lists';
    }
    return 'Other';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          Home
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/admin/hub')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          Admin Hub
        </Link>
        <Typography color="text.primary">QuickBooks Tables</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton
              onClick={() => navigate('/admin/hub')}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <StorageIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              QuickBooks Tables
            </Typography>
          </Box>
          
          {!loading && (
            <Typography variant="body2" color="text.secondary">
              {tables.length} tables synchronized from QuickBooks
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tables List */}
      {!loading && !error && tables.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Table Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Records</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tables.map((table) => (
                <TableRow
                  key={table.tableName}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleTableClick(table.tableName)}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {table.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {table.tableName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getCategory(table.tableName)}
                      size="small"
                      color={getCategoryColor(table.tableName)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1">
                      {table.recordCount.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTableClick(table.tableName);
                      }}
                    >
                      <ViewListIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Empty State */}
      {!loading && !error && tables.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No QuickBooks Tables Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Run a sync to populate the QuickBooks tables with data.
          </Typography>
        </Paper>
      )}
    </Container>
  );
}